import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import MainSpinner from 'shared/components/MainSpinner';
import AlertModal from 'shared/components/AlertModal';
import ConfirmModal from 'shared/components/ConfirmModal';

import useApi from 'hooks/useApi';

import { closeAlert, showAlert } from 'redux/actions/alertActions';

import { convertErrorsArrayToString } from 'shared/helpers/helper';
import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS, emptyString } from 'shared/utils';

import ActionBar from './ActionBar';
import EmptyCustomFolder from './EmptyCustomFolder';
import FolderFormModal from './FolderFormModal';
import CustomFoldersData from './CustomFoldersData';
import { CustomFolderWrapper } from './styles';
import SubfolderFormModal from './SubfolderFormModal';
import TranslateFormModal from './TranslateFormModal';

const CustomFolder = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { cusNavId, mail } = useSelector((state) => state.auth);
  const { openAlert, alertType, alertMessage } = useSelector((state) => state.alertState);
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const { customAreaApi } = useApi();
  const [folderRoot, setFolderRoot] = useState({});
  const [folders, setFolders] = useState([]);
  const [showFolderForm, setShowFolderForm] = useState(false);
  const [showSubfolderForm, setShowSubfolderForm] = useState(false);
  const [isFolderEdit, setIsFolderEdit] = useState(false);
  const [isSubfolderEdit, setIsSubfolderEdit] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState();
  const [selectedSubfolder, setSelectedSubfolder] = useState();
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showTranslateModal, setShowTranslateModal] = useState(false);

  const [{ loading }, getFolders] = customAreaApi(
    {
      url: `${window.API_V1}/cus/folders/${cusNavId}/${encodeURIComponent(mail)}/search`,
      method: 'POST',
      data: {
        filters: [],
        reduce: {
          limit: 0,
          skip: 0,
        },
      },
    },
    { manual: true }
  );
  const [{ loading: loadingPost }, postFolder] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );
  const [{ loading: loadingDelete }, deleteCustomFolder] = customAreaApi(
    {
      method: 'DELETE',
    },
    { manual: true }
  );

  const loadCustomFolders = async () => {
    try {
      if (loading) {
        return;
      }
      const {
        data: { errors, data },
      } = await getFolders();
      if (errors.length) {
        throw new Error(convertErrorsArrayToString(errors));
      }
      setFolderRoot(data.find((x) => !x.visible && x.pathId === '1/'));
      const newDta = data.map((d) => {
        const filterI18Name = d.i18List.find((x) => x.i18Code === selectedDefaultLanguage);
        if (filterI18Name) {
          return { ...d, i18Name: filterI18Name.i18Name };
        }
        return { ...d, i18Name: d.i18List[0].i18Name };
      });
      setFolders(
        newDta
          ? newDta
              .sort((a, b) => a.i18Name.localeCompare(b.i18Name))
              .map((folder) =>
                (folder.childsFolders && folder?.childsFolders?.length > 0) ||
                (folder.childs && folder?.childs?.length > 0)
                  ? { ...folder, isOpen: false }
                  : folder
              )
              .filter((el) => el.id !== '2')
          : []
      );
    } catch (ex) {
      console.error('Error getting custom folders: ', ex);
    }
  };

  useEffect(() => {
    setFolders([]);
    if (!emptyString(selectedDefaultLanguage)) {
      loadCustomFolders();
    }
  }, []);

  useEffect(() => {
    if (isFolderEdit && selectedFolder) {
      setShowFolderForm(true);
    }
  }, [isFolderEdit, selectedFolder]);

  useEffect(() => {
    if (isSubfolderEdit && selectedSubfolder) {
      setShowSubfolderForm(true);
    }
  }, [isSubfolderEdit, selectedSubfolder]);

  const onNewFolder = () => {
    setIsFolderEdit(false);
    setShowFolderForm(true);
  };

  const onNewSubfolder = (subfolder) => {
    setSelectedSubfolder(subfolder);
    setIsSubfolderEdit(false);
    setShowSubfolderForm(true);
  };

  const onToggleTranslate = () => {
    setShowTranslateModal(!showTranslateModal);
  };

  const onEditFolder = (folder) => {
    setIsFolderEdit(true);
    setSelectedFolder(folder);
    setShowFolderForm(true);
  };

  const onEditSubfolder = (subfolder) => {
    setIsSubfolderEdit(true);
    setSelectedSubfolder(subfolder);
    setShowSubfolderForm(true);
  };

  const onCloseFolderFormModal = () => {
    setShowFolderForm(false);
  };

  const containsSpecialCharacters = (str) => {
    const specialCharacterRegex = /[/\\:*?`<>.|]/;
    return specialCharacterRegex.test(str);
  };

  const onSaveFolderForm = async (values) => {
    try {
      const { name } = values;
      if (containsSpecialCharacters(name)) {
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_ERROR,
            alertMessage: t('documents_validation_invalid'),
          })
        );
        return;
      }
      const url = isFolderEdit
        ? `${window.API_V1}/cus/folders/${cusNavId}/${encodeURIComponent(mail)}`
        : `${window.API_V1}/cus/folders/${cusNavId}/${encodeURIComponent(mail)}`;
      const data = isFolderEdit
        ? {
            parent: folderRoot,
            childs: [
              {
                id: selectedFolder.id,
                pathId: selectedFolder.pathId,
                visible: true,
                permissions: [
                  {
                    u: mail,
                    p: 'rw-',
                  },
                ],
                i18List: [
                  {
                    i18Code: selectedDefaultLanguage,
                    i18Name: name.trim(),
                  },
                ],
              },
            ],
          }
        : {
            parent: folderRoot,
            childs: [
              {
                visible: true,
                permissions: [
                  {
                    u: mail,
                    p: 'rw-',
                  },
                ],
                i18List: [
                  {
                    i18Code: selectedDefaultLanguage,
                    i18Name: name.trim(),
                  },
                ],
              },
            ],
          };
      const {
        data: { errors },
      } = await postFolder({
        url,
        data,
      });
      if (errors.length) {
        throw new Error(convertErrorsArrayToString(errors));
      }
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_SUCCESS,
          alertMessage: t('custom_folder_folder_create_success'),
        })
      );
      loadCustomFolders();
      if (isFolderEdit) {
        setIsFolderEdit(false);
        setSelectedFolder();
      }
      onCloseFolderFormModal();
    } catch (ex) {
      console.error('Error on saveFolderForm: ', ex);
      if (ex.response?.data?.errors[0].code === 'CMDF-14') {
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_ERROR,
            alertMessage: t('Settings_error_folder_exist_msg'),
          })
        );
      } else {
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_ERROR,
            alertMessage: t('general_error_message'),
          })
        );
      }
    }
  };

  const onCloseSubfolderFormModal = () => {
    setShowSubfolderForm(false);
  };

  const onSaveSubfolderForm = async (values) => {
    try {
      const { name, pathId } = values;
      if (containsSpecialCharacters(name)) {
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_ERROR,
            alertMessage: t('documents_validation_invalid'),
          })
        );
        return;
      }
      const url = isSubfolderEdit
        ? `${window.API_V1}/cus/folders/${cusNavId}/${encodeURIComponent(mail)}`
        : `${window.API_V1}/cus/folders/${cusNavId}/${encodeURIComponent(mail)}`;
      const subFolderRoot = folders.find((x) => x.id === pathId);
      const data = isSubfolderEdit
        ? {
            parent: subFolderRoot,
            childs: [
              {
                id: selectedSubfolder.id,
                pathId: selectedSubfolder.pathId,
                visible: true,
                permissions: [
                  {
                    u: mail,
                    p: 'rw-',
                  },
                ],
                i18List: [
                  {
                    i18Code: selectedDefaultLanguage,
                    i18Name: name.trim(),
                  },
                ],
              },
            ],
          }
        : {
            parent: subFolderRoot,
            childs: [
              {
                visible: true,
                permissions: [
                  {
                    u: mail,
                    p: 'rw-',
                  },
                ],
                i18List: [
                  {
                    i18Code: selectedDefaultLanguage,
                    i18Name: name.trim(),
                  },
                ],
              },
            ],
          };
      const {
        data: { errors },
      } = await postFolder({
        url,
        data,
      });
      if (errors.length) {
        throw new Error(convertErrorsArrayToString(errors));
      }
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_SUCCESS,
          alertMessage: t('custom_folder_subfolder_create_success'),
        })
      );
      loadCustomFolders();
      if (isSubfolderEdit) {
        setIsSubfolderEdit(false);
        setSelectedSubfolder();
      }
      onCloseSubfolderFormModal();
    } catch (ex) {
      console.error('Error on saveSubfolderForm: ', ex);
      if (ex.response?.data?.errors[0].code === 'CMDF-14') {
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_ERROR,
            alertMessage: t('Settings_error_sub_folder_exist_msg'),
          })
        );
      } else {
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_ERROR,
            alertMessage: t('general_error_message'),
          })
        );
      }
    }
  };

  const onCloseAlert = () => dispatch(closeAlert());

  const toggleFolderOpen = (folderId) =>
    setFolders(
      folders.map((folder) =>
        folder.id === folderId ? { ...folder, isOpen: !folder.isOpen } : folder
      )
    );

  const onDeleteFolder = (folder) => {
    setSelectedFolder(folder);
    setShowConfirmDelete(true);
    setIsFolderEdit(false);
    setIsSubfolderEdit(false);
  };

  const onCancelDelete = () => {
    setSelectedFolder(undefined);
    setSelectedSubfolder(undefined);
    setShowConfirmDelete(false);
  };

  const getNameSelectedLanguage = (i18List) => {
    if (i18List) {
      const findName = i18List.find((x) => x.i18Code === selectedDefaultLanguage);
      if (findName && findName?.i18Name !== '') {
        return findName?.i18Name;
      }
      return i18List[0].i18Name;
    }
    return null;
  };

  const onAcceptDelete = async () => {
    try {
      if (selectedFolder) {
        const {
          data: { errors },
        } = await deleteCustomFolder({
          url: `${window.API_V1}/cus/folders/${cusNavId}/${encodeURIComponent(mail)}/${
            selectedFolder.id
          }`,
        });
        if (errors.length) {
          throw new Error(convertErrorsArrayToString(errors));
        }
      }
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_SUCCESS,
          alertMessage: t('custom_feild_delete_field_success').replace(
            '{fieldName}',
            getNameSelectedLanguage(selectedFolder?.i18List)
          ),
        })
      );
      loadCustomFolders();
      onCancelDelete();
    } catch (ex) {
      onCancelDelete();
      console.error('error deleting custom folder: ', ex);
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_ERROR,
          alertMessage: t('general_error_message'),
        })
      );
    }
  };
  return (
    <CustomFolderWrapper>
      <ActionBar onNewFolder={onNewFolder} onTranslate={onToggleTranslate} />
      {!folders.length && <EmptyCustomFolder />}
      {showFolderForm && (
        <FolderFormModal
          selectedFolder={selectedFolder}
          isEdit={isFolderEdit}
          onCloseModal={onCloseFolderFormModal}
          onSubmit={onSaveFolderForm}
        />
      )}
      {showSubfolderForm && (
        <SubfolderFormModal
          selectedSubfolder={selectedSubfolder}
          isEdit={isSubfolderEdit}
          rootFolders={folders?.filter((folder) => {
            if (!isSubfolderEdit && selectedSubfolder) {
              const pathIds = folder.pathId.split('/');
              const clearedPathIds = pathIds.filter((x) => x.length > 0);
              const parentId = clearedPathIds[clearedPathIds.length - 2];
              const subPathIds = selectedSubfolder?.pathId.split('/');
              const subClearedPathIds = subPathIds?.filter((x) => x.length > 0);
              const subParentId = subClearedPathIds[subClearedPathIds.length - 2];
              return parentId === subParentId;
            }
            const pathIds = folder.pathId.split('/');
            const clearedPathIds = pathIds.filter((x) => x.length > 0);
            const parentId = clearedPathIds[clearedPathIds.length - 2];
            return parentId;
          })}
          onCloseModal={onCloseSubfolderFormModal}
          onSubmit={onSaveSubfolderForm}
        />
      )}
      {showTranslateModal && (
        <TranslateFormModal
          folders={folders}
          onCloseModal={onToggleTranslate}
          folderRoot={folderRoot}
          loadCustomFolders={loadCustomFolders}
        />
      )}
      {folders.length >= 1 && (
        <CustomFoldersData
          folders={folders}
          onClickFolder={toggleFolderOpen}
          onEditFolder={onEditFolder}
          onEditSubfolder={onEditSubfolder}
          onDeleteFolder={onDeleteFolder}
          onNewSubfolder={onNewSubfolder}
          folderRoot={folderRoot}
        />
      )}
      <ConfirmModal
        isOpen={showConfirmDelete}
        toggle={onCancelDelete}
        confirmMessage={t('custom_folder_delete_confirmation').replace(
          '{folderName}',
          getNameSelectedLanguage((selectedFolder || selectedSubfolder)?.i18List)
        )}
        cancelText={t('general_button_no')}
        okText={t('general_button_yes')}
        onCancel={onCancelDelete}
        onOk={onAcceptDelete}
        isLoading={loadingDelete}
      />
      <AlertModal
        type={alertType}
        isOpen={openAlert}
        toggle={onCloseAlert}
        message={alertMessage}
        okText={t('general_button_ok')}
        onOk={onCloseAlert}
      />
      {(loading || loadingPost) && <MainSpinner />}
    </CustomFolderWrapper>
  );
};

export default CustomFolder;
